import { Close } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { TabContext, TabPanel } from "@mui/lab";
import { Tab } from "@mui/material";
import ExitModal from "components/NewForm/ExitModal";
import ButtonBase from "components/shared/ButtonBase";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import { StyledTabs } from "components/shared/StyledTabs";
import TitleSeparation from "components/shared/TitleSeparation";
import TransferTermFormContainer from "containers/TransferTermFormContainer";
import { transferWorkLabel } from "containers/TransferTermFormContainer/utils";
import { getCountries } from "countries";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import type { Dispatch, SetStateAction } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store/store";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import { ETransferTermSalesStatus, updateTransferTermSale } from "store/transferTermSalesStore";
import type { ITransferTerm } from "store/transferTermsStore";
import { fetchTransferTermSales } from "store/transferTermsStore";

import type { IStatusRule } from "../../../components/shared/StatusChip";
import StatusChip from "../../../components/shared/StatusChip";
import { termStatusRules } from "../TransferTermsTable";
import { getListOfCountries } from "../utils";
import AdvanceTable from "./AdvanceTable";
import AdvanceUpdateModal from "./AdvanceUpdateModal";
import AgreementTab from "./AgreementTab";
import PaymentsTable from "./PaymentsTable";
import styles from "./style.module.scss";

interface ITransferTermDrawer {
    /**
     * Data of the term selected.
     */
    transferTerm: ITransferTerm | undefined;
    /**
     * Function to toggle TransferTermDrawer.
     */
    handleToggleDrawer: (value: boolean, transferTerm?: ITransferTerm | number) => Promise<void>;
    /**
     * Status options for sales (formatted as needed for the StatusChip component)
     */
    salesStatusRules: Record<any, IStatusRule>;
    /**
     * Is drawer being accessed from a sale or a term.
     */
    from?: "sale" | "term";
    handleOpeningModal: (
        currentSale: ITransferTermSale | undefined,
        modalType: string,
        mode?: string,
        event?: any,
    ) => Promise<void>;
    /**
     * Sales payment of the term chosen.
     */
    currentSales: ITransferTermSale[];
    setCurrentSales: Dispatch<SetStateAction<ITransferTermSale[]>>;
}

/**
 * Within TransferTerms, drawer that displays details of term selected (can be accessed either from sales table or term table).
 */
const TransferTermDrawer = ({
    transferTerm,
    handleToggleDrawer,
    salesStatusRules,
    from,
    handleOpeningModal,
    currentSales,
    setCurrentSales,
}: ITransferTermDrawer) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [tabValue, setTabValue] = useState("0");
    const [editMode, setEditMode] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [blockModal, setBlockModal] = useState(false);
    const [currentTerm, setCurrentTerm] = useState<ITransferTerm | undefined>(transferTerm);
    const [currentPayment, setCurrentPayment] = useState<ITransferTermSale>();
    const [modalType, setModalType] = useState("");

    /**
     * Current term sales (only of type "sales")
     */
    const termPayments = useMemo(() => {
        return currentSales?.filter((sale) => sale.transferTermId === currentTerm?.id && sale.type === "sales");
    }, [currentTerm, currentSales]);

    /**
     * Current term advance payment (sales of type "advance")
     */
    const advancePayments = useMemo(() => {
        return currentSales?.filter((sale) => sale.transferTermId === currentTerm?.id && sale.type === "advance");
    }, [currentTerm, currentSales]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setTabValue(newValue);
    };

    /**
     * Used for edit mode of TransferTermFormContainer, to display block modal if they want to close.
     */
    const checkBeforeClosing = () => {
        if (isFormDirty) setBlockModal(true);
        else setEditMode(false);
    };

    /**
     * Updates termSale with new advance data.
     * @param status New status.
     * @param advancePayment Data of the transferTermSales being updated.
     */
    const handleAdvanceStatusChange = async (status: number, advancePayment: ITransferTermSale) => {
        const newAdvancePayment = advancePayments.find((item) => item.id === advancePayment.id);
        if (newAdvancePayment) {
            if (status === ETransferTermSalesStatus.PAYED) {
                await setCurrentPayment(newAdvancePayment);
                setModalType("updateAdvance");
            } else {
                dispatch(
                    updateTransferTermSale({ saleId: advancePayment?.id, data: { ...newAdvancePayment, status } }),
                );
            }
        }
    };

    /**
     * Used to update currentTerm when term updated as some values aren't returned in response.
     * @param updatedTerm Data from the update request.
     */
    const handleTermUpdate = (updatedTerm: ITransferTerm) => {
        const sumNetAdvance = advancePayments.reduce((a, { netAmount }) => a + netAmount, 0);
        const termNetSales = currentSales.reduce((a, { netAmount }) => a + netAmount, 0);
        const termGrossSales = currentSales.reduce((a, { grossAmount }) => a + grossAmount, 0);
        const totalGrossAmount = updatedTerm.initialGrossAmount + termGrossSales;
        const totalNetAmount =
            updatedTerm.initialGrossAmount - sumNetAdvance > 0
                ? updatedTerm.initialGrossAmount - sumNetAdvance + termNetSales
                : termNetSales;

        setCurrentTerm({ ...updatedTerm, totalGrossAmount, totalNetAmount });
        dispatch(fetchTransferTermSales({ transferTermId: updatedTerm?.id })).then((resp) => {
            if (resp.meta.requestStatus === "fulfilled") {
                setCurrentSales(resp.payload);
            }
        });
    };

    // Used to fetch sales specific to chosen term.
    useEffect(() => {
        if (transferTerm) {
            dispatch(fetchTransferTermSales({ transferTermId: transferTerm?.id })).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setCurrentSales(resp.payload);
                }
            });
        }
    }, [transferTerm]);

    return (
        <div className={styles["transfer-term-drawer"]}>
            <ExitModal
                open={blockModal}
                onConfirm={() => {
                    setEditMode(false);
                    setBlockModal(false);
                    setIsFormDirty(false);
                }}
                onCancel={() => setBlockModal(false)}
            />
            {!editMode && (
                <TabContext value={tabValue}>
                    <div className={styles["drawer-header"]}>
                        <div className={styles["header-left"]}>
                            <h1>
                                {t("TransferTerms.Drawer.TermTitle", { name: transferWorkLabel(currentTerm ?? {}) })}
                            </h1>
                            <span className={styles["transferee-name"]}>
                                {t("TransferTerms.Drawer.TermTransferee", { name: currentTerm?.transfereeName })}
                            </span>
                            <span className={styles["transferee-name"]}>
                                {t("TransferTerms.TermsStatus.TermStatus")}{" "}
                                <StatusChip status={currentTerm?.status} statusRules={termStatusRules()} />
                            </span>
                        </div>
                        <div className="header-right">
                            {from !== "sale" && (
                                <StyledButton buttonType="primary" onClick={() => setEditMode(true)}>
                                    {t("ShowDetails.EditTerm")}
                                </StyledButton>
                            )}
                            <ButtonBase
                                variant="text"
                                styles={{ border: "none", width: "unset", height: "1.9rem" }}
                                onClick={() => handleToggleDrawer(false)}
                            >
                                <CloseIcon sx={{ color: "black" }} className="term-details-close" />
                            </ButtonBase>
                        </div>
                    </div>
                    <StyledTabs value={tabValue} className={styles["drawer-tabs"]} onChange={handleTabChange}>
                        <Tab label={t("TransferTerms.Drawer.TermDetails")} value="0" />
                        <Tab label={t("TransferTerms.Drawer.PaymentHistory")} value="1" />
                        <Tab label={t("TransferTerms.Contract")} value="2" />
                    </StyledTabs>

                    {/* TERM DETAILS*/}
                    <TabPanel value="0">
                        <>
                            <div className={styles["term-details"]}>
                                <span className={styles["detail-title"]}>
                                    {t("TransferTerms.Drawer.TermLength")}
                                    <span className={styles["detail-info"]}>
                                        {currentTerm?.startDate
                                            ? formatLocaleDateString({ date: currentTerm?.startDate as Date })
                                            : "-"}
                                        {" ↔︎ "}
                                        {currentTerm?.expirationDate
                                            ? formatLocaleDateString({ date: currentTerm?.expirationDate as Date })
                                            : "-"}
                                    </span>
                                </span>
                                <span className={styles["detail-title"]}>
                                    {t("TransferTerms.Drawer.TermPaymentFrequence")}
                                    <span className={styles["detail-info"]}>
                                        {t("TransferTerms.Drawer.FrequenceMonthsAmount", {
                                            number: currentTerm?.periodDuration,
                                        })}
                                    </span>
                                </span>
                                <span className={styles["detail-title"]}>
                                    {t("TransferTerms.Drawer.TermCountries")}
                                    <span className={styles["detail-info"]}>
                                        {currentTerm?.countryCodes &&
                                            getListOfCountries(currentTerm?.countryCodes, getCountries())}
                                    </span>
                                </span>
                                <div className={styles["advance-payment-info"]}>
                                    <TitleSeparation
                                        title={t("TransferTerms.Drawer.AdvanceHistory")}
                                        titleStyle={{ fontSize: "0.87rem", color: "grey" } as React.CSSProperties}
                                    />
                                    <AdvanceTable
                                        advancePayments={advancePayments}
                                        onAdvanceChange={handleAdvanceStatusChange}
                                    />
                                </div>
                            </div>
                        </>
                    </TabPanel>

                    {/* PAYMENT HISTORY */}
                    <TabPanel value="1">
                        <div className={styles["term-details"]}>
                            {!!currentTerm?.initialGrossAmount && currentTerm?.initialGrossAmount > 0 && (
                                <span className={styles["detail-title"]}>
                                    {t("TransferTerms.Drawer.RightsBalance")}
                                    <span className={styles["detail-info"]}>
                                        {formatNumber(currentTerm?.initialGrossAmount / 100, { isCurrency: true })}
                                    </span>
                                </span>
                            )}
                            <span className={styles["detail-title"]}>
                                {t("TransferTerms.Drawer.TotalGrossSales")}
                                <span className={styles["detail-info"]}>
                                    {formatNumber((currentTerm?.totalGrossAmount ?? 0) / 100, { isCurrency: true })}
                                </span>
                            </span>
                            <span className={styles["detail-title"]}>
                                {t("TransferTerms.Drawer.TotalNetSales")}
                                <span className={styles["detail-info"]}>
                                    {formatNumber((currentTerm?.totalNetAmount ?? 0) / 100, { isCurrency: true })}
                                </span>
                            </span>
                            <PaymentsTable
                                termSales={termPayments}
                                salesStatusRules={salesStatusRules}
                                handleOpeningModal={handleOpeningModal}
                            />
                        </div>
                    </TabPanel>

                    {/* AGREEMENT */}
                    <TabPanel value="2">
                        <AgreementTab
                            transferTerm={currentTerm}
                            onAgreementUpload={(term) => setCurrentTerm({ ...transferTerm, ...term })}
                        />
                    </TabPanel>
                </TabContext>
            )}
            {editMode && (
                <div className={styles["term-form-edit-mode"]}>
                    <Close className={styles["edit-close-btn"]} onClick={checkBeforeClosing} />
                    <TransferTermFormContainer
                        mode="edit"
                        closeEdit={() => setEditMode(false)}
                        term={currentTerm}
                        setIsFormDirty={setIsFormDirty}
                        onEdit={(term) => handleTermUpdate({ ...transferTerm, ...term })}
                        onDelete={() => handleToggleDrawer(false)}
                        termAdvances={advancePayments}
                        termSales={currentSales}
                    />
                </div>
            )}
            <ModalBase
                open={modalType === "updateAdvance"}
                onClose={() => setModalType("")}
                modalTitle={t("TransferTerms.Drawer.PayAdvance")}
                modalSubtitle={t("TransferTerms.Drawer.PayAdvanceSubtitle")}
            >
                <AdvanceUpdateModal
                    currentPayment={currentPayment}
                    handleClose={() => setModalType("")}
                    setCurrentSales={setCurrentSales}
                    currentSales={currentSales}
                />
            </ModalBase>
        </div>
    );
};

export default React.memo(TransferTermDrawer);
